import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  LoadingState,
  Dialog,
  Field,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import { useAuth } from "../../lib/auth";
import { api, reviewApi, type ReviewLens, type ReviewRecord, type ReviewWorkItem } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { BLOCKING_SEVERITY_LABEL, REVIEW_STATE_LABEL, stateLabel, stateTone } from "./labels";
import { interactionStateFor } from "./interactionStates";
import { InteractionStatePresenter } from "./interactionStatePresenter";
import { memberName, type ReviewMember } from "./memberNames";

function WorkTable({ title, rows }: { title: string; rows: ReviewWorkItem[] }) {
  return (
    <Card>
      <CardHeader title={title} />
      <CardBody>
        {rows.length === 0 ? (
          <EmptyState title={`No ${title.toLowerCase()}`} description="Review AI has no open work of this type." />
        ) : (
          <DataTable
            caption={title}
            rows={rows.map((row) => ({ ...row, id: row.id }))}
            columns={[
              {
                key: "title",
                header: "Review",
                render: (row) => <Link to={`/studio/review-ai/reviews/${row.reviewId}`}>{row.title}</Link>,
              },
              { key: "assignee", header: "Assigned to", render: (row) => row.assignedTo },
              { key: "due", header: "Due", render: (row) => (row.dueAt ? formatDate(row.dueAt) : "Not set") },
            ]}
          />
        )}
      </CardBody>
    </Card>
  );
}

function workItems(lenses: ReviewLens[], reviews: ReviewRecord[], members: ReviewMember[]): {
  assigned: ReviewWorkItem[];
  overdue: ReviewWorkItem[];
  awaiting: ReviewWorkItem[];
} {
  const byId = new Map(reviews.map((review) => [review.id, review]));
  const assigned: ReviewWorkItem[] = [];
  const overdue: ReviewWorkItem[] = [];
  for (const lens of lenses) {
    const review = byId.get(lens.reviewId);
    const row: ReviewWorkItem = {
      id: `assigned-${lens.id}`,
      type: "review.lens_assigned",
      reviewId: lens.reviewId,
      lensId: lens.id,
      title: review?.purpose ?? lens.lensKey,
      assignedTo: memberName(members, lens.assignedReviewerId),
      dueAt: lens.dueAt,
      sourceRevision: lens.revision,
    };
    if (lens.state === "ASSIGNED") assigned.push(row);
    if (lens.state === "ASSIGNED" && lens.dueAt && new Date(lens.dueAt).getTime() < Date.now()) {
      overdue.push({ ...row, id: `overdue-${lens.id}`, type: "review.lens_overdue" });
    }
  }
  const awaiting = reviews
    .filter((review) => review.lenses.length > 0 &&
      review.lenses.every((lens) => !lens.required || lens.state === "CONCLUDED") &&
      review.lifecycleState !== "CONCLUDED")
    .map((review) => ({
      id: `outcome-${review.id}`,
      type: "review.awaiting_outcome" as const,
      reviewId: review.id,
      title: review.purpose,
      assignedTo: memberName(members, review.ownerUserId),
      dueAt: review.dueAt,
      sourceRevision: review.revision,
    }));
  return { assigned, overdue, awaiting };
}

export function ReviewDashboard() {
  const { activeOrg, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [requestOpen, setRequestOpen] = useState(false);
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [purpose, setPurpose] = useState("");
  const [ownerId, setOwnerId] = useState(user?.id ?? "");
  const [dueAt, setDueAt] = useState("");
  const [criteriaSetId, setCriteriaSetId] = useState("");
  const [criteriaName, setCriteriaName] = useState("");
  const [criterionCode, setCriterionCode] = useState("");
  const [criterionText, setCriterionText] = useState("");
  const [criteriaBlocking, setCriteriaBlocking] = useState("NONE");
  const [actionError, setActionError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const orgId = activeOrg?.id ?? "";
  const documentId = searchParams.get("documentId");
  const members = useAsync<ReviewMember[]>(async () => (await api.members(orgId)).members, [orgId]);
  const state = useAsync(
    async () => {
      const [reviewsResponse, lensesResponse, criteriaResponse] = await Promise.all([
        reviewApi.listReviews(orgId),
        reviewApi.actionableLenses(orgId),
        reviewApi.listCriteriaSets(orgId),
      ]);
      return { reviews: reviewsResponse.reviews, lenses: lensesResponse.lenses, criteriaSets: criteriaResponse.criteriaSets };
    },
    [orgId],
  );
  const allReviews = state.data?.reviews ?? [];
  const documentReviews = documentId
    ? allReviews.filter((review) => review.scopeItems.some((scope) => scope.documentId === documentId))
    : allReviews;
  const subjectResolved = documentId ? documentReviews.length > 0 : true;
  useEffect(() => {
    if (documentId && documentReviews.length === 1) {
      navigate(`/studio/review-ai/reviews/${documentReviews[0]!.id}`, { replace: true });
    }
  }, [documentId, documentReviews, navigate]);
  if (state.loading && !state.data) return <LoadingState label="Loading Review AI…" />;
  if (state.error) {
    const interactionState = interactionStateFor(state.error);
    return <InteractionStatePresenter state={interactionState} retry={state.reload} />;
  }
  if (documentId && documentReviews.length === 1) return null;
  const reviews = subjectResolved ? documentReviews : allReviews;
  const work = workItems(state.data?.lenses ?? [], reviews, members.data ?? []);
  async function createAndPublishCriteria() {
    if (!criteriaName.trim() || !criterionCode.trim() || !criterionText.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      const created = await reviewApi.createCriteriaSet(orgId, { name: criteriaName.trim(), blockingSeverity: criteriaBlocking });
      await reviewApi.addCriterion(orgId, created.criteriaSet.id, {
        code: criterionCode.trim(), criterionText: criterionText.trim(), blockingSeverity: criteriaBlocking, position: 1,
      });
      await reviewApi.publishCriteriaSet(orgId, created.criteriaSet.id);
      setCriteriaOpen(false);
      setCriteriaName(""); setCriterionCode(""); setCriterionText("");
      state.reload();
    } catch (error) {
      setActionError(error);
    } finally { setSaving(false); }
  }
  async function requestReview() {
    if (!purpose.trim() || !ownerId || !dueAt || !criteriaSetId) return;
    setSaving(true);
    setActionError(null);
    try {
      await reviewApi.createReview(orgId, { purpose: purpose.trim(), ownerUserId: ownerId, dueAt: new Date(dueAt).toISOString(), criteriaSetId });
      setRequestOpen(false); setPurpose(""); setDueAt("");
      state.reload();
    } catch (error) { setActionError(error); } finally { setSaving(false); }
  }
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Review AI</h1>
          <p>Review records showing who reviewed what, against which criteria, and what they concluded.</p>
          {documentId && subjectResolved ? (
            <p role="status">Showing reviews whose pinned scope covers document {documentId}.</p>
          ) : null}
          {documentId && !subjectResolved ? (
            <p role="status">This document subject could not be resolved; showing the unfiltered Review AI landing surface.</p>
          ) : null}
        </div>
        <div className="ods-row">
          <Button onClick={() => { setActionError(null); setCriteriaOpen(true); }}>Author criteria set</Button>
          <Button variant="secondary" onClick={() => { setActionError(null); setRequestOpen(true); }}>Request review</Button>
        </div>
      </div>
      <h2 className="ods-visually-hidden">Review workspace</h2>
      <Card>
        <CardHeader title="Reviews" />
        <CardBody>
          {reviews.length === 0 ? (
            <EmptyState title="No reviews" description="There are no Review AI records for this organization." />
          ) : (
            <DataTable
              caption="Reviews"
              rows={reviews}
              columns={[
                {
                  key: "purpose",
                  header: "Review",
                  render: (review) => <Link to={`/studio/review-ai/reviews/${review.id}`}>{review.purpose}</Link>,
                },
                {
                  key: "state",
                  header: "State",
                  render: (review) => <Badge tone={stateTone(review.lifecycleState)}>{stateLabel(review.lifecycleState, REVIEW_STATE_LABEL)}</Badge>,
                },
                { key: "due", header: "Due", render: (review) => formatDate(review.dueAt) },
              ]}
            />
          )}
        </CardBody>
      </Card>
      <div className="ods-stack" style={{ marginTop: "var(--ods-space-4)" }}>
        <WorkTable title="Assigned lenses" rows={work.assigned} />
        <WorkTable title="Overdue lenses" rows={work.overdue} />
        <WorkTable title="Reviews awaiting an outcome" rows={work.awaiting} />
      </div>
      <Dialog open={criteriaOpen} title="Author and publish criteria set"
        description={`You are acting as ${user?.displayName ?? user?.email ?? "the current user"}. Draft criteria are published as an immutable version.`}
        onClose={() => setCriteriaOpen(false)} dismissible={!saving}
        footer={<><Button variant="secondary" onClick={() => setCriteriaOpen(false)}>Cancel</Button><Button onClick={createAndPublishCriteria} disabled={saving || !criteriaName.trim() || !criterionCode.trim() || !criterionText.trim()}>{saving ? "Publishing…" : "Create draft and publish"}</Button></>}>
        <Field label="Criteria set name">{(id) => <TextInput id={id} value={criteriaName} onChange={(e) => setCriteriaName(e.target.value)} />}</Field>
        <Field label="Set blocking severity">{(id) => <Select id={id} value={criteriaBlocking} onChange={(e) => setCriteriaBlocking(e.target.value)}>{Object.entries(BLOCKING_SEVERITY_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select>}</Field>
        <Field label="Criterion code">{(id) => <TextInput id={id} value={criterionCode} onChange={(e) => setCriterionCode(e.target.value)} />}</Field>
        <Field label="Criterion text">{(id) => <TextArea id={id} rows={3} value={criterionText} onChange={(e) => setCriterionText(e.target.value)} />}</Field>
        <p>Publishing freezes this criteria-set version. Add another draft version to change it later.</p>
        {actionError ? <InteractionStatePresenter state={interactionStateFor(actionError)} retry={() => setActionError(null)} /> : null}
      </Dialog>
      <Dialog open={requestOpen} title="Request a review"
        description={`You are acting as ${user?.displayName ?? user?.email ?? "the current user"}. This governed request is recorded by Review AI.`}
        onClose={() => setRequestOpen(false)} dismissible={!saving}
        footer={<><Button variant="secondary" onClick={() => setRequestOpen(false)}>Cancel</Button><Button onClick={requestReview} disabled={saving || !purpose.trim() || !ownerId || !dueAt || !criteriaSetId}>{saving ? "Requesting…" : "Request review"}</Button></>}>
        <Field label="Purpose">{(id) => <TextInput id={id} value={purpose} onChange={(e) => setPurpose(e.target.value)} />}</Field>
        <Field label="Accountable owner">{(id) => <Select id={id} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>{(members.data ?? []).map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}</Select>}</Field>
        <Field label="Due date">{(id) => <TextInput id={id} type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />}</Field>
        <Field label="Published criteria set">{(id) => <Select id={id} value={criteriaSetId} onChange={(e) => setCriteriaSetId(e.target.value)}><option value="">Select a published set</option>{(state.data?.criteriaSets ?? []).filter((set) => set.state === "PUBLISHED").map((set) => <option key={set.id} value={set.id}>{set.name} v{set.version}</option>)}</Select>}</Field>
        {actionError ? <InteractionStatePresenter state={interactionStateFor(actionError)} retry={() => setActionError(null)} /> : null}
      </Dialog>
    </>
  );
}
